import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import Classes from "./Navbar.module.css";
import { scrollToElement } from "../../../../../utils/scrollToElement";

const NAV_ITEMS = [
    { label: "Dryer Vent Cleaning", path: "/dryer-vent-cleaning" },
    { label: "Appliance Repair", path: "/appliance-repair" },
    { label: "Service Areas", id: "service-areas", offset: 20 },
    { label: "Benefits", id: "benefits", offset: 20 },
    { label: "FAQ", id: "faq", offset: 25 },
];

function PhoneIcon({ className = "" }) {
    return (
        <svg
            className={className}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <path
                d="M6.6 10.8c1.6 3 3.6 5 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.9.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C11 21 3 13 3 3c0-.6.4-1 1-1h3.1c.6 0 1 .4 1 1 0 1.4.2 2.7.6 3.9.1.4 0 .8-.3 1.1L6.6 10.8Z"
                fill="currentColor"
            />
        </svg>
    );
}

export default function Navbar() {
    const navigateTo = useNavigate();
    const location = useLocation();

    const [mobileOpen, setMobileOpen] = useState(false);

    // close mobile menu when route changes
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // close on ESC
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") setMobileOpen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const handleScrollOrNavigate = (id, offset = 0) => {
        setMobileOpen(false);

        if (location.pathname !== "/") {
            navigateTo("/", { state: { scrollTo: { id, offset } } });
        } else {
            scrollToElement(id, offset);
        }
    };

    const onNavItemClick = (item) => {
        if (item.path) {
            setMobileOpen(false);
            navigateTo(item.path);
        } else {
            handleScrollOrNavigate(item.id, item.offset);
        }
    };

    const onLogoClick = (e) => {
        e.preventDefault();
        setMobileOpen(false);

        if (location.pathname !== "/") {
            navigateTo("/");
        } else {
            // optional: scroll to top
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const phoneNumberDisplay = "(561) 929-5476";
    const phoneNumberTel = useMemo(
        () => "tel:+15619295476",
        []
    );

    return (
        <motion.header className={Classes.navbar}>
            <div className={Classes.inner}>
                {/* Left: Logo */}
                <a href="/" onClick={onLogoClick} className={Classes.logoLink}>
                    <img
                        className={Classes.logoImg}
                        draggable={false}
                        loading="lazy"
                        decoding="async"
                        src={"/assets/icons/logo.png"}
                        alt="Quick Vents and Appliance Repair logo"
                    />
                </a>

                {/* Center: Nav (desktop) */}
                <nav className={Classes.centerNav} aria-label="Primary">
                    <ul className={Classes.navList}>
                        {NAV_ITEMS.map((item) => (
                            <li key={item.label} className={Classes.navItem}>
                                {item.path ? (
                                    <a
                                        href={item.path}
                                        className={Classes.navBtn}
                                        onClick={(e) => { e.preventDefault(); onNavItemClick(item); }}
                                    >
                                        {item.label}
                                    </a>
                                ) : (
                                    <button
                                        type="button"
                                        className={Classes.navBtn}
                                        onClick={() => onNavItemClick(item)}
                                    >
                                        {item.label}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Right: Contact + Hours (desktop) + Hamburger (mobile) */}
                <div className={Classes.rightArea}>
                    <div className={Classes.rightInfo}>
                        <a
                            className={Classes.infoItem}
                            href={phoneNumberTel}
                            aria-label={`Call ${phoneNumberDisplay}`}
                        >
                            <PhoneIcon className={Classes.phoneIcon} />
                            <span className={Classes.infoText}>{phoneNumberDisplay}</span>
                        </a>

                        <div className={Classes.infoItem} aria-label="Business hours">
                            <span className={Classes.dot} aria-hidden="true" />
                            <span className={Classes.infoText}>
                                Open 24/7
                            </span>
                        </div>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        type="button"
                        className={Classes.menuBtn}
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label={mobileOpen ? "Close menu" : "Open menu"}
                        aria-expanded={mobileOpen}
                    >
                        <span className={Classes.menuBars} aria-hidden="true" />
                    </button>
                </div>
            </div>

            {/* Mobile dropdown */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        className={Classes.mobileMenu}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18 }}
                    >
                        <div className={Classes.mobileNav}>
                            {NAV_ITEMS.map((item) => (
                                <button
                                    key={item.label}
                                    type="button"
                                    className={Classes.mobileNavBtn}
                                    onClick={() => onNavItemClick(item)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
